"use client";

import { motion, useReducedMotion } from "framer-motion";
import { LoopSketch } from "@/components/brand/LoopSketch";
import { Accent, SectionHeader } from "@/components/ui/SectionHeader";

const EASE = [0.16, 1, 0.3, 1] as const;

const STEPS = [
  {
    title: "Discovery call",
    body: "You walk us through what you want to build. We ask the right questions and make sure we understand the problem before we suggest anything.",
  },
  {
    title: "Plan and proposal",
    body: "We come back with a clear plan: what we will build, how it will work, and what it will cost. No vague estimates, no hidden scope.",
  },
  {
    title: "Build with check-ins",
    body: "Our team starts work. You get regular updates as it takes shape, and your feedback shapes every round.",
  },
  {
    title: "Handover",
    body: "We ship the finished work to you, tested and documented. You get everything: the repo, the files, the access. Yours from day one.",
  },
];

export const About = () => {
  const reduce = useReducedMotion();

  return (
    <section id="about" aria-labelledby="about-heading" className="relative bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[95vw] px-4 sm:max-w-[90vw] sm:px-6 md:max-w-[1400px] md:px-8">
        <SectionHeader
          headingId="about-heading"
          size="lg"
          title={
            <>
              How we <Accent>work.</Accent>
            </>
          }
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: reduce ? 0 : 0.8, ease: EASE }}
          className="mt-12 grid items-center gap-12 md:mt-16 md:grid-cols-12 md:gap-8"
        >
          {/* Left: sketch fills the column, vertically centred */}
          <div className="md:col-span-5">
            <LoopSketch className="w-full text-brand-ink" />
          </div>

          {/* Right: step list */}
          <div className="md:col-span-6 md:col-start-7 md:-mt-8">
            <ul className="divide-y divide-black/10 border-t border-black/10">
              {STEPS.map((step) => (
                <li key={step.title} className="py-4">
                  <h3 className="text-lg font-bold text-brand-ink">{step.title}</h3>
                  <p className="mt-1 text-base leading-relaxed text-brand-ink/55">{step.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
